import React from 'react';
import { Helmet } from 'react-helmet-async';
import Button from './Button';
import ServiceShowcase from './ServiceShowcase';
import { BRAND, brandify } from '../brandConfig';

const faqs = [
  {
    q: 'What does it cost to hire someone to build a website for my business?',
    a: brandify('Wind Marketing website design packages for small business start at $299 setup plus $59/month for a 4-page responsive site. The Pro Website plan ($499 setup + $99/month) adds full SEO optimization, AI search optimization, a custom domain, and priority content updates. Every package includes design, development, hosting configuration, and contact form integration — no hidden hourly billing.'),
  },
  {
    q: `What does ${BRAND} include in their small business web design service?`,
    a: brandify('Wind Marketing builds custom websites tailored to your brand, including responsive design for all devices, conversion optimization, SEO setup, contact form integration, and domain configuration. Every site is engineered to make a strong first impression and drive measurable results.'),
  },
  {
    q: 'How long does it take to build a small business website?',
    a: brandify('Project timelines vary by scope, but most small business websites are delivered within 2–4 weeks from kickoff. Wind Marketing works closely with clients from day one to ensure the final product reflects your brand and meets your business goals.'),
  },
  {
    q: 'Is hiring a website designer better than using a website builder for my small business?',
    a: brandify('DIY website builders like Wix or Squarespace work for hobby projects, but a professionally designed website typically converts more visitors into customers. Wind Marketing handles design, copy structure, SEO, speed optimization, and ongoing updates — so you run your business instead of wrestling with templates. At $59–$99/month, professional design costs about the same as premium builder plans.'),
  },
  {
    q: `Does ${BRAND} build SEO-optimized websites?`,
    a: brandify("Yes. Wind Marketing's Pro Website plan includes full SEO and AI optimization, ensuring your site is structured for both search engine ranking and AI answer engine visibility."),
  },
];

const WebDesignPage = ({ navigate }) => {
  return (
    <div className="py-32 max-w-[95vw] mx-auto px-4 md:px-8 bg-background min-h-screen flex flex-col justify-between">
      <Helmet>
        <title>{`Small Business Website Design Services | ${BRAND}`}</title>
        <meta name="description" content={brandify('Professional web design services for small business. Wind Marketing builds affordable, conversion-focused websites — packages from $299 setup + $59/mo. Chicago-based, serving businesses nationwide.')} />
        <meta name="keywords" content={`small business website designer, web design services for small business, business website design services, affordable web design for small business, website design packages, ${BRAND} web design`} />
        <link rel="canonical" href="https://www.wind-marketing.com/web-design" />
        <meta property="og:title" content={`Small Business Website Design Services • ${BRAND}`} />
        <meta property="og:description" content={brandify('Professional web design services for small business. Affordable, conversion-focused websites from $299 setup + $59/mo.')} />
        <meta property="og:url" content="https://www.wind-marketing.com/web-design" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Small Business Website Design",
            "provider": {
              "@type": "ProfessionalService",
              "@id": "https://www.wind-marketing.com/#organization",
              "name": "${BRAND}",
              "url": "https://www.wind-marketing.com"
            },
            "description": "${brandify('Professional web design services for small business. Wind Marketing builds affordable, high-impact, conversion-focused websites that drive engagement, leads, and measurable results.')}",
            "url": "https://www.wind-marketing.com/web-design",
            "serviceType": "Website Design",
            "areaServed": [
              { "@type": "City", "name": "Chicago" },
              { "@type": "Country", "name": "United States" }
            ],
            "offers": {
              "@type": "Offer",
              "priceCurrency": "USD",
              "price": "299",
              "description": "Small business website packages from $299 setup + $59/month"
            }
          }
        `}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map(({ q, a }) => ({
            '@type': 'Question',
            name: q,
            acceptedAnswer: { '@type': 'Answer', text: a },
          })),
        })}</script>
      </Helmet>
      {/* Main Content Area */}
      <div className="max-w-4xl mx-auto mt-12 mb-20 flex-grow w-full">
        {/* Header Section */}
        <div className="w-full mb-27">
          <h1
            className="text-4.5xl whitespace-nowrap sm:text-6xl md:text-8xl lg:text-9xl font-bold uppercase tracking-tighter leading-none mb-8 text-foreground"
            style={{ fontFamily: "'Poppins Black', sans-serif" }}
          >
            WEB DESIGN
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-medium">
            A killer website is easily the sharpest tool a brand can leverage. Most people jump online the second they need a product or service, meaning your site dictates that critical first impression. It's essentially your 24/7 digital storefront. We collaborate with you from day one to build an online presence that speaks your language and puts your best foot forward.
          </p>
        </div>

        <ServiceShowcase currentPath="/web-design" navigate={navigate} />

        {/* Packages teaser */}
        <section className="mb-32">
          <details className="group border-2 border-border mb-8">
            <summary className="cursor-pointer list-none p-6 flex justify-between items-center gap-4 select-none">
              <h2 className="text-xl md:text-2xl font-bold uppercase tracking-tighter text-foreground">
                Website Design Packages for Small Business
              </h2>
              <span className="text-accent font-black text-2xl transition-transform duration-200 group-open:rotate-45 flex-shrink-0">+</span>
            </summary>
            <p className="text-lg text-muted-foreground leading-relaxed px-6 pb-6">
              Our web design services for small business are built for owners who'd rather run their company than wrestle with DIY builders. Whether you're looking to hire someone to build a website for your business or replace a template site that isn't converting, affordable web design shouldn't mean cheap results. Packages start at <strong className="text-foreground">$299 setup + $59/month</strong> for a fully custom, responsive 4-page site — design, development, and contact forms included. The Pro plan adds full SEO and AI optimization, a custom domain, and priority content updates for $499 setup + $99/month. Based in Chicago, serving small businesses nationwide.
            </p>
          </details>
          <button
            onClick={() => navigate('/pricing')}
            className="w-full uppercase tracking-tighter font-bold rounded-none transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background bg-accent text-accent-foreground hover:scale-105 active:scale-95 p-6 flex justify-center items-center text-xl"
          >
            View Pricing &amp; Packages
          </button>
        </section>

        {/* FAQ Section (collapsed accordions — content stays in the DOM so search engines index it) */}
        <section className="mt-20 mb-16">
          <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-tighter mb-10 text-foreground">
            Common Questions
          </h2>
          <div className="space-y-4">
            {faqs.map(({ q, a }) => (
              <details key={q} className="group border-2 border-border">
                <summary className="cursor-pointer list-none p-6 flex justify-between items-center gap-4 select-none">
                  <h3 className="text-lg md:text-xl font-bold uppercase tracking-tighter text-foreground">{q}</h3>
                  <span className="text-accent font-black text-2xl transition-transform duration-200 group-open:rotate-45 flex-shrink-0">+</span>
                </summary>
                <p className="text-lg text-muted-foreground leading-relaxed px-6 pb-6">{a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Local link */}
        <p className="text-lg text-muted-foreground mb-12">
          Looking for a website designer near you in Chicagoland?{' '}
          <a
            href="/web-design-chicago"
            onClick={(e) => { e.preventDefault(); navigate('/web-design-chicago'); }}
            className="text-accent font-bold hover:underline"
          >
            Chicago website design services →
          </a>
        </p>

        {/* Interactive Buttons */}
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-12">
          <Button
            size="large"
            onClick={() => navigate('/contact', { serviceInterest: 'web design' })}
          >
            Start Your Website Project
          </Button>
          <Button
            variant="outline"
            size="large"
            onClick={() => navigate('/work')}
          >
            OUR SERVICES
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WebDesignPage;
